import { useContext } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { ApprovalTick } from "static/media";
import { authorization } from "module/auth";
import { FeatureFlagContext } from "context/featureFlagContext";

import { CampaignInfoFormData } from "campaign-builder/domain/schemas/campaignInformation";
import { CampaignDetailsSection } from "./components/CampaignDetailsSection";
import { TargetingSection } from "./components/TargetingSection";

import "./CampaignSummaryCard.css";
import { TotalSection } from "./components/TotalSection/TotalSection";
import { PrimaryButton, SecondaryButton } from "./buttons";
import { Audience } from "campaign-builder/domain/types";

export type CampaignSummaryCardProps = {
  buttonText: string;
  secondaryButtonText?: string;
  campaign: Partial<CampaignInfoFormData>;
  flyerQuote: {
    flyersAmount: number;
    discounts?: Array<{
      title: string;
      price: number;
      quantity: number;
    }>;
    totalCost: number;
  } | null;
  isNextEnabled: boolean;
  onClickNext: () => void;
  onClickSecondaryButton?: () => void;
  onEditFlyerFormat?: () => void;
  targeting?: Audience[];
  isLoading?: boolean;
  isReadOnly?: boolean;
  reviewMeetingDate?: number;
  internalStatus?: string;
  checkingCampaignName?: boolean;
  isAudienceLoading?: boolean;
  isCampaignInfoLoading?: boolean;
  isCampaignDetailsLoading?: boolean;
  // EDDM-specific props
  isEddm?: boolean;
  onStartDateChange?: (date: Date | null) => void;
  launchDateError?: string | null;
  timezone?: string;
  isSubmitted?: boolean;
  // Campaign name editing
  onCampaignNameChange?: (name: string) => Promise<{ success: boolean; error?: string }>;
};

export const CampaignSummaryCard = ({
  campaign,
  flyerQuote,
  buttonText,
  targeting,
  onClickNext,
  isNextEnabled,
  secondaryButtonText,
  onClickSecondaryButton,
  isLoading,
  onEditFlyerFormat,
  isReadOnly,
  reviewMeetingDate,
  internalStatus,
  checkingCampaignName,
  isAudienceLoading,
  isCampaignInfoLoading,
  isCampaignDetailsLoading,
  // EDDM-specific props
  isEddm,
  onStartDateChange,
  launchDateError,
  timezone,
  isSubmitted,
  // Campaign name editing
  onCampaignNameChange,
}: CampaignSummaryCardProps) => {
  const isAdmin = authorization.isAdmin();
  const { featureFlags } = useContext(FeatureFlagContext);
  const isCampaignSaving =
    isAudienceLoading || checkingCampaignName || isCampaignInfoLoading || isCampaignDetailsLoading;

  const renderMainCTA = () => {
    if (isAdmin && featureFlags.isAdminADMOrderDetailsEnabled) {
      return (
        <div className="summaryCardButtonContainer primaryButtonPadding" data-cypress-id={"summaryCardButtonContainer"}>
          <PrimaryButton disabled={!isNextEnabled || isLoading} onClick={onClickNext}>
            {buttonText}
          </PrimaryButton>
        </div>
      );
    }

    if (isReadOnly) {
      return;
    }

    return (
      <div className="summaryCardButtonContainer primaryButtonPadding">
        <PrimaryButton disabled={!isNextEnabled || isLoading} onClick={onClickNext}>
          {buttonText}
        </PrimaryButton>
      </div>
    );
  };

  return (
    <Box className="summaryCardContainer" data-cypress-id={"campaignSummaryCard"}>
      <Box className="campaignSummaryContainer" data-cypress-id={"campaignSummaryContainer"}>
        {isLoading && (
          <Box
            className="summaryLoadingOverlay"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(255, 255, 255, 0.7)"
            zIndex={1}
          >
            <CircularProgress size={40} color="secondary" />
          </Box>
        )}
        <CampaignDetailsSection
          isReadOnly={isReadOnly}
          campaign={campaign}
          onEditFlyerFormat={onEditFlyerFormat}
          campaignStatus={{ internalStatus, reviewMeetingDate }}
          isEddm={isEddm}
          onStartDateChange={onStartDateChange}
          launchDateError={launchDateError}
          timezone={timezone}
          isSubmitted={isSubmitted}
          onCampaignNameChange={onCampaignNameChange}
        />
        {!!targeting?.length && <TargetingSection audiences={targeting} />}
        {flyerQuote &&
          TotalSection({
            total: flyerQuote.totalCost,
            discounts: flyerQuote.discounts,
            units: flyerQuote.flyersAmount,
          })}
      </Box>
      {isCampaignSaving ? (
        <div className="savingChanges">
          <CircularProgress size={20} style={{ color: "#4F5B94" }} />
          Saving changes
        </div>
      ) : (
        <div className="savingChanges" data-cypress-id={"savingChanges"}>
          <ApprovalTick />
          <span className="campaignSavedText" style={{ color: "#063206" }}>
            Campaign saved
          </span>
        </div>
      )}
      {renderMainCTA()}
      <div className="summaryCardButtonContainer" data-cypress-id={"summaryCardButtonContainer"}>
        {secondaryButtonText && (
          <SecondaryButton disabled={isLoading} onClick={onClickSecondaryButton}>
            {secondaryButtonText}
          </SecondaryButton>
        )}
      </div>
    </Box>
  );
};
